// The wind tunnel's own state: the results of testing the current plan, and of testing one advisor's
// play against the same seeds. Kept out of src/state/store.ts on purpose — a batch is a side errand the
// planner runs while the planning clock ticks, and nothing in the live fight may ever wait on it.
//
// It outlives the planning rail's scroll position and the planning stage itself, so the live stage can
// show a one-chip reminder of what the plan was measured at (`WindTunnelChip` in
// src/ui/planning/WindTunnel.tsx). `reset` is only called when a new area of operations is chosen.

import { create } from 'zustand';
import { cancel as cancelBatch, runBatch } from '../sim/batchClient';
import type { BatchPlanOrder, BatchResult } from '../sim/batchRun';
import { TICKS_PER_MIN } from '../sim/types';
import { templatePlay } from '../ai/templates';
import type { Persona } from '../ai/prompts';
import { advisorContext, useAdvisors } from './advisors';
import { useStore } from './store';

/** How many times a plan is flown. Twenty runs of the fitted 240-minute scenario is about six seconds. */
export const SEED_COUNT = 20;

/** Which batch is in flight: the player's own plan, or one advisor's play. */
export type BatchSubject = 'plan' | Persona;

export interface BatchRunState {
  subject: BatchSubject;
  /** What the plan was called on screen, so a stale result still says what it measured. */
  label: string;
  orderCount: number;
  results: BatchResult[];
}

interface WindTunnelState {
  /** The batch in flight, or null. */
  running: BatchSubject | null;
  progress: { done: number; total: number };
  error: string | null;
  /** The last completed test of the player's own plan. */
  plan: BatchRunState | null;
  /** The last completed test of an advisor's play, for the side-by-side comparison. */
  compare: BatchRunState | null;

  testPlan: () => Promise<void>;
  testAdvisor: (persona: Persona) => Promise<void>;
  reset: () => void;
}

/** The persona names as the rail writes them. */
export const PERSONA_LABEL: Record<Persona, string> = {
  maneuver: 'maneuver advisor',
  fires: 'fires advisor',
  recon: 'reconnaissance advisor',
};

/**
 * The orders one advisor would open with: its current play if the rail has one, otherwise the template
 * play that stands in for the model (src/ai/templates.ts). Either way it is a complete, legal opening
 * built from the same task organization the player's plan is built from, so the two are comparable.
 */
export function advisorPlan(persona: Persona): { orders: BatchPlanOrder[]; source: 'advisor' | 'template' } {
  const live = useAdvisors.getState().plays[persona]?.validated.orders;
  if (live && live.length > 0) return { orders: live.map((o) => ({ ...o })), source: 'advisor' };
  const ctx = advisorContext('planning');
  if (!ctx) return { orders: [], source: 'template' };
  const play = templatePlay(persona, ctx);
  return { orders: play.orders.map((o) => ({ unitId: o.unitId, card: o.card, target: o.target })), source: 'template' };
}

export const useWindTunnel = create<WindTunnelState>((set, get) => ({
  running: null,
  progress: { done: 0, total: SEED_COUNT },
  error: null,
  plan: null,
  compare: null,

  async testPlan() {
    const plan = useStore.getState().plan.map((o) => ({ ...o }));
    await run(set, get, 'plan', 'your plan', plan);
  },

  async testAdvisor(persona) {
    const { orders } = advisorPlan(persona);
    await run(set, get, persona, PERSONA_LABEL[persona], orders);
  },

  reset() {
    cancelBatch();
    set({ running: null, progress: { done: 0, total: SEED_COUNT }, error: null, plan: null, compare: null });
  },
}));

// A distribution belongs to one area of operations and one fitted scenario. Choosing another AO makes
// every number here a lie about a different piece of ground, so they go.
let lastAo: string | null = null;
useStore.subscribe((s) => {
  const ao = s.scenario?.ao ?? null;
  if (ao === lastAo) return;
  lastAo = ao;
  useWindTunnel.getState().reset();
});

type SetState = (partial: Partial<WindTunnelState>) => void;
type GetState = () => WindTunnelState;

/**
 * One batch, start to finish. The seeds are `scenario.seed + i`, so the same plan on the same area of
 * operations always meets the same twenty enemies — the comparison between two plays is a comparison of
 * the plays, not of the dice.
 */
async function run(
  set: SetState,
  get: GetState,
  subject: BatchSubject,
  label: string,
  plan: BatchPlanOrder[],
): Promise<void> {
  if (get().running !== null) return;
  const { meta, buffers, scenario, dials } = useStore.getState();
  if (!meta || !buffers || !scenario) { set({ error: 'No area of operations loaded' }); return; }
  if (plan.length === 0) { set({ error: 'Nothing to test: this plan has no orders' }); return; }

  const seeds = Array.from({ length: SEED_COUNT }, (_, i) => scenario.seed + i);
  set({ running: subject, error: null, progress: { done: 0, total: seeds.length } });
  try {
    const results = await runBatch(
      { meta, buffers, scenario, dials, plan, seeds, maxTicks: scenario.timeLimitMin * TICKS_PER_MIN },
      (done, total) => set({ progress: { done, total } }),
    );
    const state: BatchRunState = { subject, label, orderCount: plan.length, results };
    set(subject === 'plan' ? { plan: state, compare: null, running: null } : { compare: state, running: null });
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    set({ running: null, error: message === 'batch cancelled' ? null : message });
  }
}

// ---- reading a batch -------------------------------------------------------------------------------

export interface Band { p10: number; p90: number; n: number }

export interface BatchSummary {
  total: number;
  seized: number;
  culminated: number;
  outOfTime: number;
  /** Ticks from H-hour to the first blue platoon on the objective, over the runs that got there. */
  timeToObjective: Band | null;
  /** Blue platoons destroyed or broken, per run. */
  casualties: Band | null;
  /** Ticks from H-hour to the first enemy contact reported, over the runs that made contact. */
  firstContact: Band | null;
  /** The commonest lost-time items across the batch, worst first. */
  causes: { item: string; count: number }[];
}

/** The 10th and 90th percentiles of a sample, by nearest rank. A band, not an average: the point of a
 * distribution is the spread, and a mean hides exactly the run that goes wrong. */
export function band(values: number[]): Band | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const at = (q: number): number => sorted[Math.min(sorted.length - 1, Math.max(0, Math.round(q * (sorted.length - 1))))];
  return { p10: at(0.1), p90: at(0.9), n: sorted.length };
}

/** How many failure causes the panel lists. */
const TOP_CAUSES = 3;

export function summarize(results: BatchResult[]): BatchSummary {
  const counts = new Map<string, number>();
  for (const r of results) {
    // One run votes at most once per item, so "twelve runs lost time here" means twelve runs.
    for (const item of new Set(r.lostTime.map((lt) => lt.item))) {
      counts.set(item, (counts.get(item) ?? 0) + 1);
    }
  }
  return {
    total: results.length,
    seized: results.filter((r) => r.ended === 'seized').length,
    culminated: results.filter((r) => r.ended === 'culminated').length,
    // A run that hit the tick budget without an end condition is out of time too: the clock beat it.
    outOfTime: results.filter((r) => r.ended === 'time' || r.ended === null).length,
    timeToObjective: band(results.map((r) => r.objectiveReachedTick).filter((t): t is number => t !== null)),
    casualties: band(results.map((r) => r.blue.destroyed + r.blue.broken)),
    firstContact: band(results.map((r) => r.firstContactTick).filter((t): t is number => t !== null)),
    causes: [...counts.entries()]
      .map(([item, count]) => ({ item, count }))
      .sort((a, b) => b.count - a.count || a.item.localeCompare(b.item))
      .slice(0, TOP_CAUSES),
  };
}
